import { useMemo } from 'react';

/**
 * The useContentfulImage is used to generate the props required on the Image component in the RedBubble design system.
 * Contentful provides an api that allows you to request modified images from their CDN.
 * We use this throughout the app to ensure that we are providing images at the most appropriate resolution, format and quality.
 *
 * The hook itself takes two arguments, a url and an options object.
 * The options object is optional however if you do not include options with a imageSizes property you will only be returned a src prop in the object.
 * The imageSizes array should have its content organised in mobile to desktop order.
 * Each object in imageSizes should have a mediaQuery property set to ensure the image will be visible unless it is the final "fallback" image where no mediaQuery property is required.
 */

/**
 * Example Usage
 * For more info look at the useContentfulImag.test.js file colocated with this file.
 */

/*
Simple options - preferred

const contentfulImageUrl = '//images.ctfassets.net/image.jpg';
const options = {
  imageSizes: [
    {
      mediaQuery: '(max-width: 768px)',
      imageWidth: 150
    },
    { 
      mediaQuery: '',
      imageWidth: 360
    }
  ],
  fileExtension: 'jpg',
  quality: 85
};

will return 

const imageProps = {
  src: '//images.ctfassets.net/image1.jpg?fm=jpg&q=85',
  srcSet: '//images.ctfassets.net/image1.jpg?fm=jpg&q=85&w=150 150w, //images.ctfassets.net/image1.jpg?fm=jpg&q=85&w=300 300w',
  sizes: '(max-width: 768px) 150px,  300px'
}
*/

/*
Advanced options

Multiple image srcs could be used for different breakpoints
In this case an contentfulImageUrl is not required and the largest image will be used for the src prop

Important: There is a potential for the client to use incorrect images on different screens and breakpoints.
This is because the client will cache the largest image size and use it on smaller image sizes if a viewport is resized to a smaller size.
This is also because retina screens will be provided with larger images.
This problem will persist until we have the option of using the <picture> element in our design system.

const options = {
  imageSizes: [
    {
      imageUrl: 'http://test.url/image1.jpg',
      mediaQuery: '(max-width: 768px)',
      imageWidth: 150
    },
    { 
      imageUrl: 'http://test.url/image2.jpg',
      mediaQuery: '',
      imageWidth: 300
    }
  ],
  fileExtension: 'jpg',
  quality: 85
}

will return 

const imageProps = {
  src: '//images.ctfassets.net/image2.jpg?fm=jpg&q=85',
  srcSet: '//images.ctfassets.net/image1.jpg?fm=jpg&q=85&w=150 150w, //images.ctfassets.net/image2.jpg?fm=jpg&q=85&w=300 300w',
  sizes: '(max-width: 768px) 150px,  300px'
}
*/

// regexr.com/6l2os
const contentfulUrlRegex = /^(https:|http:|)?\/\/images\.ctfassets\.net/i;

type ImageSize = {
  imageUrl?: string;
  mediaQuery: string;
  imageWidth: number;
};

type UseContentfulImageArgs = {
  imageSizes?: ImageSize[]; // Image sizes array, this value should be memoised before being passed to hook.
  fileExtension?: string; // 'jpg' | 'webp' | 'png'; Image file extension
  progressive?: boolean; // Provide progressive image, fileExtension must be set to 'jpg'
  quality?: number; // Set image quality 1 - 100
};

type ImageProps = {
  src: string;
  srcSet: string;
  sizes: string;
};

const useContentfulImage = (
  {
    imageSizes = [],
    fileExtension = 'jpg',
    progressive = false,
    quality = 85,
  }: UseContentfulImageArgs = {},
  contentfulImageUrl: string | null = ''
): ImageProps => {
  const imageProps = useMemo(() => {
    const mainImage =
      contentfulImageUrl ||
      (imageSizes[imageSizes.length - 1] &&
        imageSizes[imageSizes.length - 1].imageUrl);

    // early exit if no image provided
    if (!mainImage) {
      return {
        src: '',
        srcSet: '',
        sizes: '',
      };
    }

    const isContentfulUrl = contentfulImageUrl
      ? contentfulImageUrl.match(contentfulUrlRegex)
      : true;
    const imageSizesIsContentfulUrl =
      imageSizes.filter((size) => {
        // if no image url return false
        if (!size.imageUrl) return false;
        // if doesnt match return false
        return !size.imageUrl.match(contentfulUrlRegex);
      }).length === 0;

    // early exit if contentfulImageUrl or any of the urls supplied in imageSizes are not contentful urls
    if (!isContentfulUrl || !imageSizesIsContentfulUrl) {
      return {
        src: mainImage,
        srcSet: '',
        sizes: '',
      };
    }

    const isJpg = fileExtension === 'jpg';
    const isProgressive = progressive && isJpg ? '&fl=progressive' : '';
    const clampedQuality = Math.min(Math.max(1, quality), 100);

    const srcSet = imageSizes.reduce((prev, curr, i) => {
      const imageUrl = `${
        curr.imageUrl || mainImage
      }?fm=${fileExtension}&q=${clampedQuality}&w=${
        curr.imageWidth
      }${isProgressive}`;
      return `${prev}${i !== 0 ? ', ' : ''}${imageUrl} ${curr.imageWidth}w`;
    }, '');

    const sizes = imageSizes.reduce((prev, curr, i) => {
      const mediaQuery = curr.mediaQuery ? `${curr.mediaQuery} ` : '';
      return `${prev}${i !== 0 ? ', ' : ''}${mediaQuery}${curr.imageWidth}px`;
    }, '');

    const src = `${
      contentfulImageUrl || imageSizes[imageSizes.length - 1].imageUrl
    }?fm=${fileExtension}&q=${clampedQuality}${isProgressive}`;

    return { src, srcSet, sizes };
  }, [contentfulImageUrl, imageSizes, fileExtension, quality, progressive]);
  return imageProps;
};

export { useContentfulImage };
